import Dialog from '@components/atoms/dialog/dialog';
import TagContainer from '@enums/tagContainer';
import { useAnalytics } from '@context/analytics.context';
import { useNegotiation } from '@context/negotiation.context';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import useLocalStorage from '@hooks/use-local-storage';
import React, { HTMLProps, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import {
  useGetActiveNegotiation,
  useGetNegotiationById,
  useGetNegotiationInitialData,
} from '@hooks/negotiations/useNegotationQueries';
import { NegotiationDialogStyles } from '@constants/negotiation';
import dynamic from 'next/dynamic';
import useProductQuery from '@hooks/product/use-product-query';
import useHeroPdpStyles from '@hooks/hero-pdp/use-hero-pdp-styles';
import useUser from '@hooks/user/use-user';
import { NegotiationEvent } from '@interfaces/models/negotiation/negotiationEvent';
import CTAButtonsBlock from './components/CTA-buttons-block';
import CTANegotiationBlock from './components/CTA-negotiation-block';
import { CTAProductContext } from './context';

const NegotiationModal = dynamic(
  () => import('@components/organisms/negotiation/negotiation-modal/negotiation-modal').then((x) => x.NegotiationModal),
  {
    ssr: false,
  },
);

const CTANegotiationMessageBlock = dynamic(() => import('./components/CTA-negotiation-message-block'), {
  ssr: false,
});

type CTAProductProps = Pick<HTMLProps<HTMLDivElement>, 'className'>;

const CTAProduct: React.FC<CTAProductProps> = ({ className }): React.JSX.Element => {
  const { negotiation } = useNegotiation();
  const router = useRouter();
  const { sendEvent } = useAnalytics();
  const { sendAnalyticEvent: sendNegotiationAnalyticEvents } = useAnalyticEvents('mmao');
  const { isAuthenticated } = useUser();
  const { product, userTransactionRole } = useProductQuery();
  const { isHeroPdpStyles } = useHeroPdpStyles();
  const [sellerBackHolidayAlerts, setSellerBackHolidayAlerts] = useLocalStorage('sellerBackHolidayAlerts', []);
  const [openNegotiationModal, setOpenNegotiationModal] = useState<boolean>(false);
  const [holidaysEndAlertId, setHolidaysEndAlertId] = useState<string>(null);
  const [showFpdSection, setShowFpdSection] = useState<boolean>(false);
  const queryParams = router.query;
  const negotiationId = String(queryParams.negotiation_id || '');
  const showFpd = queryParams.showFpd === 'true';
  const isBuyer = userTransactionRole === NegotiationEvent.CreatedByEnum.Buyer;
  const shouldShowNegotiationMessageBlock =
    Boolean(negotiation?.buyerRemainingOffers || negotiation?.sellerRemainingOffers) ||
    negotiation?.nextActions?.length;
  const shouldShowNegotiationBanner = isBuyer && product?.buyersWhoHaveActiveNegotiation?.length > 0;

  useGetNegotiationById({
    negotiationId: negotiationId,
    enabled: Boolean(isAuthenticated && negotiationId),
    onSuccess: () => {
      setShowFpdSection(showFpd);
      setOpenNegotiationModal(true);
    },
  });
  const { isLoading: isLoadingActiveNegotiation, isError: hasActiveNegotiationError } = useGetActiveNegotiation({
    productId: product.id,
    enabled: isAuthenticated && product.negotiable && isBuyer,
  });
  useGetNegotiationInitialData({
    buyerId: null,
    productId: product.id,
    enabled: Boolean(
      isAuthenticated && !isLoadingActiveNegotiation && (hasActiveNegotiationError || negotiation?.isClosed),
    ),
  });

  const trackOnClickNegotiation = () => {
    sendNegotiationAnalyticEvents('pdp_make_an_offer');
    if (isAuthenticated) {
      setOpenNegotiationModal(true);
    }
  };

  const closeNegotiationModal = (): void => {
    setOpenNegotiationModal(false);
    sendEvent({
      type: 'event',
      container: TagContainer.Analytics,
      payload: {
        event: 'popinClose',
        popinName: 'MMAO',
      },
    });
  };

  useEffect(() => {
    sellerBackHolidayAlerts.forEach((item: unknown) => {
      const propertyNames = Object.getOwnPropertyNames(item);
      if (propertyNames[0] === product.id) {
        setHolidaysEndAlertId(item[propertyNames[0]]);
      }
    });
  }, [product.id, setHolidaysEndAlertId, sellerBackHolidayAlerts]);

  return (
    <CTAProductContext
      value={{
        sellerBackHolidayAlerts,
        holidaysEndAlertId,
        setSellerBackHolidayAlerts,
        setHolidaysEndAlertId,
        trackOnClickNegotiation,
      }}
    >
      {shouldShowNegotiationMessageBlock && <CTANegotiationMessageBlock />}
      {shouldShowNegotiationBanner && (
        <CTANegotiationBlock
          concurrentActiveNegotiations={product.buyersWhoHaveActiveNegotiation.length}
          isNegotiationAccepted={negotiation?.isAccepted}
          trackingFrom="mmao_ongoing_banner_pdp"
          showBuyNow={false}
          applyTopMargin={!isHeroPdpStyles}
          product={product}
        />
      )}
      {isBuyer && <CTAButtonsBlock customClass={className} />}
      <Dialog
        closeDialog={closeNegotiationModal}
        key={`openNegotiationModal${openNegotiationModal}`}
        isOpen={openNegotiationModal}
        hasCloseIcon
        styles={{
          content: NegotiationDialogStyles,
        }}
      >
        <NegotiationModal
          trackingFrom="mmao_ongoing_banner"
          onClose={closeNegotiationModal}
          showFpdSection={showFpdSection}
        />
      </Dialog>
    </CTAProductContext>
  );
};

export default CTAProduct;
